// import Vue from 'vue';
import { createRouter, createWebHistory } from 'vue-router';

import { getConferenceParamsFromToken, getRedirectBasedOnEmailConfirmed, getRedirectForCallConfirmation, onRouteInitialize, getGlobalNumberBasedOnRouteQuery, createConferenceCallGuard } from '@/service/routerService';
import Login from '@/pages/Login/';
import Register from '@/pages/Register/';
import Disclaimer from '@/pages/Disclaimer/';
import Validation from '@/pages/EmailValidation/';
import CallConfirmation from '@/pages/CallConfirmation/';
import SupplementalRegister from '@/pages/SupplementalRegister/';
import ConferenceEnded from '@/pages/ConferenceEnded/';
import GlobalNumbers from '@/pages/GlobalNumbers/';

import store from './store';

const router = createRouter({
	history: createWebHistory(process.env.VUE_APP_BASE_URL),
	routes: [{
		path: '/login',
		name: 'login',
		component: Login,
		beforeEnter: async (to, from, next) => {
			await createConferenceCallGuard(to, from, next, { shouldRedirect: true });
		}
	}, {
		path: '/login/:uniqueId',
		name: 'login-new',
		component: Login,
		beforeEnter: async (to, from, next) => {
			const params = await getConferenceParamsFromToken(to.params.uniqueId);
			await createConferenceCallGuard(to, from, next, {
				showId: params.show,
				confId: params.confId,
				shouldRedirect: true
			});
		}
	}, {
		path: '/register',
		name: 'register',
		component: Register,
		beforeEnter: async (to, from, next) => {
			await createConferenceCallGuard(to, from, next, { shouldRedirect: true });
		},
		props: true,
	}, {
		path: '/register/:uniqueId',
		name: 'register-new',
		component: Register,
		beforeEnter: async (to, from, next) => {
			const params = await getConferenceParamsFromToken(to.params.uniqueId);
			await createConferenceCallGuard(to, from, next, {
				showId: params.show,
				confId: params.confId,
				shouldRedirect: true
			});
		},
		props: true,
	}, {
		path: '/validation',
		name: 'validation',
		component: Validation,
		beforeEnter: async (to, from, next) => {
			await createConferenceCallGuard(to, from, next, { shouldRedirect: true });
		},
		props: true,
	}, {
		path: '/validation/:uniqueId',
		name: 'validation-new',
		component: Validation,
		beforeEnter: async (to, from, next) => {
			const params = await getConferenceParamsFromToken(to.params.uniqueId);
			await createConferenceCallGuard(to, from, next, {
				showId: params.show,
				confId: params.confId,
				shouldRedirect: true
			});
		},
		props: true,
	}, {
		path: '/404-not-found',
		name: '404-not-found',
		beforeEnter: async (to, from, next) => {
			store.dispatch('global/setIsNotFoundView', true);
			next();
		}
	}, {
		path: '/site-not-available',
		name: 'site-not-available',
		beforeEnter: async (to, from, next) => {
			store.dispatch('global/setIsSiteNotAvailable', true);
			next();
		}
	},{
		path: '/conference-ended',
		name: 'conference-ended',
		component: ConferenceEnded,
		beforeEnter: async (to, from, next) => {
			await createConferenceCallGuard(to, from, next, { shouldRedirect: false });
		}
	}, {
		path: '/conference-ended/:uniqueId',
		name: 'conference-ended-new',
		component: ConferenceEnded,
		beforeEnter: async (to, from, next) => {
			const params = await getConferenceParamsFromToken(to.params.uniqueId);
			await createConferenceCallGuard(to, from, next, {
				showId: params.show,
				confId: params.confId,
				shouldRedirect: false
			});
		}
	}, {
		path: '/email-confirmation',
		name: 'email-confirmation',
		beforeEnter: async (to, from, next) => {
			let nextRoute = await getRedirectBasedOnEmailConfirmed(to.query.RowGuid, to.query.ShowLink, to.query.confId);
			next(nextRoute);
		}
	}, {
		path: '/email-confirmation/:uniqueId',
		name: 'email-confirmation-new',
		beforeEnter: async (to, from, next) => {
			let params = await getConferenceParamsFromToken(to.params.uniqueId);
			let nextRoute = await getRedirectBasedOnEmailConfirmed(to.query.RowGuid, params.show, params.confId);
			next(nextRoute);
		}
	}, {
		path: '/disclaimer',
		name: 'disclaimer',
		component: Disclaimer,
		beforeEnter: async (to, from, next) => {
			await onRouteInitialize(to.query.show);
			store.dispatch('presentation/fetchDisclaimerContent');
			next();
		},
	}, {
		path: '/disclaimer/:uniqueId',
		name: 'disclaimer-new',
		component: Disclaimer,
		beforeEnter: async (to, from, next) => {
			const params = await getConferenceParamsFromToken(to.params.uniqueId);
			await onRouteInitialize(params.show);
			store.dispatch('presentation/fetchDisclaimerContent');
			next();
		},
	},{
		path: '/call-confirmation',
		name: 'call-confirmation',
		component: CallConfirmation,
		beforeEnter: async (to, from, next) => {
			await onRouteInitialize(to.query.show);
			const nextRoute = await getRedirectForCallConfirmation(to.query.show, to.query.confId, to.query.email);
			next(nextRoute);
		},
	}, {
		path: '/call-confirmation/:uniqueId',
		name: 'call-confirmation-new',
		component: CallConfirmation,
		beforeEnter: async (to, from, next) => {
			const params = await getConferenceParamsFromToken(to.params.uniqueId);
			await onRouteInitialize(params.show);
			const nextRoute = await getRedirectForCallConfirmation(params.show, params.confId, to.query.email);
			next(nextRoute);
		},
	}, {
		path: '/supplemental-register',
		name: 'supplemental-register',
		component: SupplementalRegister,
		beforeEnter: async (to, from, next) => {
			await onRouteInitialize(to.query.show);
			await createConferenceCallGuard(to, from, next, { shouldRedirect: true });
		},
		props: true,
	}, {
		path: '/supplemental-register/:uniqueId',
		name: 'supplemental-register-new',
		component: SupplementalRegister,
		beforeEnter: async (to, from, next) => {
			const params = await getConferenceParamsFromToken(to.params.uniqueId);
			await onRouteInitialize(params.show);
			await createConferenceCallGuard(to, from, next, {
				showId: params.show,
				confId: params.confId,
				shouldRedirect: true
			});
		},
		props: true,
	},{
		path: '/global-numbers',
		name: 'global-numbers',
		component: GlobalNumbers,
		beforeEnter: async (to, from, next) => {
			store.dispatch('global/setIsAdminView', true);
			let nextRoute = await getGlobalNumberBasedOnRouteQuery(to.query.confId, to.query.presId, to.query.sgmMeetingId);
			if (nextRoute === '/404-not-found') {
				store.dispatch('global/setIsAdminView', false);
			}
			next(nextRoute);
		}
	},{
		path: '/global-numbers/:uniqueId',
		name: 'global-numbers-new',
		component: GlobalNumbers,
		beforeEnter: async (to, from, next) => {
			store.dispatch('global/setIsAdminView', true);
			const params = await getConferenceParamsFromToken(to.params.uniqueId);
			let nextRoute = await getGlobalNumberBasedOnRouteQuery(params.confId, params.presId, to.query.sgmMeetingId);
			if (nextRoute === '/404-not-found') {
				store.dispatch('global/setIsAdminView', false);
			}
			next(nextRoute);
		}
	},{
		path: '/:pathMatch(.*)*',
		redirect: '/404-not-found',
		beforeEnter: async (to, from, next) => {
			store.dispatch('global/setIsNotFoundView', true);
			next();
		}
	}, {
		path: '/',
		name: '',
		beforeEnter: async (to, from, next) => {
			window.location.href = window.location.origin;
		}		
	}]
});

export default router;